import { useEffect, useState, useMemo } from 'react'

import { useForm } from '@mantine/form'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import { zodResolver } from 'mantine-form-zod-resolver'

import { useMutation, useQuery } from '@redwoodjs/web'

import { trackContactAdded } from 'src/components/Inputs/CarrierAutocomplete/contactTelemetry'
import { KnownContact } from 'src/components/Inputs/CarrierAutocomplete/knownContacts'
import { useContactChoice } from 'src/components/Inputs/CarrierAutocomplete/useContactChoice'
import { useKnownCarrierContacts } from 'src/components/Inputs/CarrierAutocomplete/useKnownCarrierContacts'
import { toast } from 'src/components/Overlays/Toast/Toast'
import {
  GET_APPLICATION,
  GET_APPLICATION_BY_APPLICANT_ID,
} from 'src/graphql/application.graphql'
import {
  GET_EMPLOYMENT_EXPERIENCE,
  UPDATE_EMPLOYMENT_EXPERIENCE,
  DELETE_EMPLOYMENT_EXPERIENCE,
} from 'src/graphql/employmentExperiences.graphql'
import { EmploymentExperienceSchema } from 'src/validations'

dayjs.extend(utc)

const parseMonthYearToDate = (dateString?: string | null) => {
  if (!dateString) {
    return null
  }

  const parsed = dayjs.utc(dateString)

  if (!parsed.isValid()) {
    return null
  }

  return new Date(parsed.year(), parsed.month(), 1)
}

const formatDateToUtcMonthStart = (date?: Date | null) => {
  if (!date) {
    return null
  }

  return dayjs(date).utc().startOf('month').toISOString()
}

const CONTACT_COMPONENT = 'EmploymentDetailsDrawer'

const EMPTY_ADDRESS = {
  street: '',
  city: '',
  state: '',
  zipCode: '',
  country: '',
}

/** The stored entry as form values. Used to load, to cancel, and by "Use". */
const valuesFromExperience = (employmentExperience) => ({
  companyName: employmentExperience.companyName || '',
  position: employmentExperience.position || '',
  startDate: parseMonthYearToDate(employmentExperience.startDate),
  endDate: parseMonthYearToDate(employmentExperience.endDate),
  dotNumber: employmentExperience.dotNumber || '',
  contactName: employmentExperience.contactName || '',
  email: employmentExperience.email || '',
  phone: employmentExperience.phone || '',
  faxNumber: employmentExperience.faxNumber || '',
  reasonForLeaving: employmentExperience.reasonForLeaving || '',
  unemploymentReason: employmentExperience.unemploymentReason || '',
  equipmentTypes: employmentExperience.equipmentTypes || [],
  address: employmentExperience.address || { ...EMPTY_ADDRESS },
  isDriverTerminated: employmentExperience.isDriverTerminated ?? false,
  isDriverSubjectToFMCSRs: employmentExperience.isDriverSubjectToFMCSRs ?? true,
  isJobDesignatedAsSafetySensitiveFunctionUnderDOT:
    employmentExperience.isJobDesignatedAsSafetySensitiveFunctionUnderDOT ??
    true,
  shouldContactEmployer: employmentExperience.shouldContactEmployer ?? false,
})

/** The UpdateEmploymentExperience input for a set of form values. */
const buildInput = (values): Record<string, unknown> => {
  // Prepare address data - always include to support clearing addresses
  const addressData = values.address as {
    street?: string
    city?: string
    state?: string
    zipCode?: string
    country?: string
  } | null

  return {
    companyName: values.companyName,
    position: values.position,
    startDate: formatDateToUtcMonthStart(values.startDate),
    endDate: formatDateToUtcMonthStart(values.endDate),
    dotNumber: values.dotNumber || null,
    email: values.email || null,
    contactName: values.contactName || null,
    phone: values.phone || null,
    faxNumber: values.faxNumber || null,
    reasonForLeaving: values.reasonForLeaving || null,
    unemploymentReason: values.unemploymentReason || null,
    isDriverTerminated: values.isDriverTerminated,
    isDriverSubjectToFMCSRs: values.isDriverSubjectToFMCSRs,
    isJobDesignatedAsSafetySensitiveFunctionUnderDOT:
      values.isJobDesignatedAsSafetySensitiveFunctionUnderDOT,
    shouldContactEmployer: values.shouldContactEmployer,
    equipmentTypes: values.equipmentTypes,
    // Always include address to support clearing/updating existing addresses
    address: addressData
      ? {
          street: addressData.street?.trim() || '',
          city: addressData.city?.trim() || '',
          state: addressData.state?.trim() || '',
          zipCode: addressData.zipCode?.trim() || '',
          country: addressData.country?.trim() || '',
        }
      : { ...EMPTY_ADDRESS },
  }
}

export const useEmploymentExperience = (
  employmentExperienceId: string,
  onDeleteSuccess?: () => void
) => {
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false)

  // Query to get the specific employment experience
  const { data: employmentExperienceData, loading: queryLoading } = useQuery(
    GET_EMPLOYMENT_EXPERIENCE,
    {
      variables: { employmentExperienceId },
      skip: !employmentExperienceId,
    }
  )

  // Extract the employment experience from the query data
  const employmentExperience = employmentExperienceData?.employmentExperience

  // Define refetch queries - always refetch all three queries
  const getRefetchQueries = () => {
    const applicationId = employmentExperience?.application?.applicationId
    const applicantId =
      employmentExperience?.application?.applicant?.applicantId

    return [
      {
        query: GET_EMPLOYMENT_EXPERIENCE,
        variables: { employmentExperienceId },
      },
      {
        query: GET_APPLICATION,
        variables: { id: applicationId },
      },
      {
        query: GET_APPLICATION_BY_APPLICANT_ID,
        variables: { applicantId },
      },
    ]
  }

  const form = useForm({
    initialValues: {
      companyName: '',
      position: '',
      startDate: null,
      endDate: null,
      dotNumber: '',
      contactName: '',
      email: '',
      phone: '',
      faxNumber: '',
      reasonForLeaving: '',
      unemploymentReason: '',
      equipmentTypes: [],
      address: {
        street: '',
        city: '',
        state: '',
        zipCode: '',
        country: '',
      },
      isDriverTerminated: false,
      isDriverSubjectToFMCSRs: true,
      isJobDesignatedAsSafetySensitiveFunctionUnderDOT: true,
      shouldContactEmployer: false,
    },
    validate: zodResolver(EmploymentExperienceSchema),
  })

  // Update employment experience mutation
  const [updateEmploymentExperience, { loading: updateLoading }] = useMutation(
    UPDATE_EMPLOYMENT_EXPERIENCE,
    {
      refetchQueries: getRefetchQueries,
      awaitRefetchQueries: true,
      onCompleted: () => {
        toast('Employment experience updated successfully', 'success')
        setHasUnsavedChanges(false)
        form.resetDirty()
      },
      onError: (error) => {
        console.error('Error updating employment experience:', error)
        toast('Failed to update employment experience', 'error')
      },
    }
  )

  // Delete employment experience mutation
  const [deleteEmploymentExperience, { loading: deleteLoading }] = useMutation(
    DELETE_EMPLOYMENT_EXPERIENCE,
    {
      refetchQueries: getRefetchQueries,
      awaitRefetchQueries: true,
      onCompleted: () => {
        toast('Employment experience deleted successfully', 'success')
        onDeleteSuccess?.()
      },
      onError: (error) => {
        console.error('Error deleting employment experience:', error)
        toast('Failed to delete employment experience', 'error')
      },
    }
  )

  // Track form changes
  useEffect(() => {
    setHasUnsavedChanges(form.isDirty())
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.values])

  // Reset form when employment experience data loads
  useEffect(() => {
    if (!employmentExperience) return

    form.setValues(valuesFromExperience(employmentExperience))
    form.resetDirty()
    setHasUnsavedChanges(false)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [employmentExperience])

  // Contacts the directory holds for the stored entry's DOT: the read-mode
  // block. Same query and variables as the picker's, so one request serves both
  const storedKnownContacts = useKnownCarrierContacts(
    employmentExperience?.dotNumber
  )

  // Contact provenance while editing: prefill, pick, clear, save telemetry
  const contactChoice = useContactChoice({
    component: CONTACT_COMPONENT,
    dotNumber: form.values.dotNumber as string,
    values: form.values,
    setContactFields: ({ contactName, phone, email }) => {
      form.setFieldValue('contactName', contactName)
      form.setFieldValue('phone', phone)
      form.setFieldValue('email', email)
    },
  })

  /** Runs the update; resolves true when it saved. Errors are toasted by the mutation. */
  const saveInput = async (input: Record<string, unknown>) => {
    try {
      const result = await updateEmploymentExperience({
        variables: {
          employmentExperienceId,
          input,
        },
      })
      return Boolean(result?.data?.updateEmploymentExperience)
    } catch (error) {
      return false
    }
  }

  const handleSave = () => {
    const validation = form.validate()
    if (validation.hasErrors) {
      return
    }

    if (!employmentExperienceId) {
      toast('Unable to save: missing employment experience ID', 'error')
      return
    }

    // Decided now, while the form holds what is being saved; sent on success
    const emitContactTelemetry = contactChoice.prepareSaveTelemetry(
      employmentExperience
        ? {
            contactName: employmentExperience.contactName,
            phone: employmentExperience.phone,
            email: employmentExperience.email,
          }
        : null
    )

    void saveInput(buildInput(form.values)).then((saved) => {
      if (!saved) return
      emitContactTelemetry()
      // The entry now holds this contact: a later save is not a new choice
      contactChoice.reset()
    })
  }

  /**
   * "Use" on the read-mode contacts block: saves the contact onto the entry
   * straight away, without entering edit mode. Built from the stored entry,
   * not the form, so nothing but the three contact fields changes.
   *
   * The stored dates go back exactly as they came. The form's path parses a
   * month into a local-time Date and formats it back to a UTC month start,
   * which lands one month early for a recruiter east of UTC; a one-click Use
   * must not move an employment window the recruiter never touched.
   */
  const handleUseContact = async (contact: KnownContact) => {
    if (!employmentExperience || !employmentExperienceId) return

    const saved = await saveInput({
      ...buildInput({
        ...valuesFromExperience(employmentExperience),
        contactName: contact.contactName || '',
        phone: contact.phoneNumber || '',
        email: contact.email || '',
      }),
      startDate: employmentExperience.startDate ?? null,
      endDate: employmentExperience.endDate ?? null,
    })
    if (!saved) return

    trackContactAdded({
      component: CONTACT_COMPONENT,
      source: 'known_default',
      contact,
      knownCount: storedKnownContacts.count,
      dotNumber: employmentExperience.dotNumber ?? null,
    })
  }

  const handleCancel = () => {
    contactChoice.reset()
    if (!employmentExperience) return

    form.setValues(valuesFromExperience(employmentExperience))

    setTimeout(() => {
      form.resetDirty()
      setHasUnsavedChanges(false)
    }, 0)
  }

  // Handle delete employment experience
  const handleDelete = async () => {
    if (!employmentExperienceId) {
      toast('Unable to delete: missing employment experience ID', 'error')
      return
    }

    try {
      await deleteEmploymentExperience({
        variables: {
          employmentExperienceId,
        },
      })
    } catch (error) {
      // Error handling is done in the mutation's onError callback
    }
  }

  // Handle company selection from FMCSA search
  const handleCompanySelect = (selected: {
    companyName?: string
    dotNumber?: string
    phone?: string
    fax?: string
    address?: {
      street?: string
      city?: string
      state?: string
      zipCode?: string
      country?: string
    }
  }) => {
    if (!selected) return

    // Update individual form fields to trigger proper form state management
    form.setFieldValue('companyName', selected?.companyName || '')
    form.setFieldValue('dotNumber', selected?.dotNumber || '')
    form.setFieldValue('faxNumber', selected?.fax || '')
    form.setFieldValue('address', {
      street: selected?.address?.street || '',
      city: selected?.address?.city || '',
      state: selected?.address?.state || '',
      zipCode: selected?.address?.zipCode || '',
      country: selected?.address?.country || '',
    })

    // Blank contact fields take the directory's top contact for this DOT
    contactChoice.requestPrefill(selected?.dotNumber)
  }

  const handleContactSelected = (selected: KnownContact | null) => {
    contactChoice.selectKnownContact(selected)
  }

  // Memoize form validation to prevent infinite re-renders
  const isFormValid = useMemo(() => {
    return !form.validate().hasErrors
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.values])

  const loading = queryLoading || updateLoading || deleteLoading

  return {
    employmentExperience,
    form,
    hasUnsavedChanges,
    loading,
    queryLoading,
    handleSave,
    handleCancel,
    handleDelete,
    handleCompanySelect,
    handleContactSelected,
    isFormValid,
    // Read mode: contacts held for the stored entry's DOT, and "Use"
    knownContacts: storedKnownContacts.contacts,
    knownContactCount: storedKnownContacts.count,
    directoryCarrierId: storedKnownContacts.carrierId,
    handleUseContact,
    useContactSaving: updateLoading,
    // Edit mode: provenance of the contact in the form, and the picker hint
    contactChoice: contactChoice.contactChoice,
    clearSuggestedContact: contactChoice.clearSuggested,
    contactHintProps: contactChoice.contactHintProps,
  }
}
